import React from 'react';
import {
  Box,
  Heading,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  Button,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  TableCaption,
  TableContainer,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import qs from 'qs';
import dayjs from 'dayjs';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { TeacherProvider, useIndex } from '../index/teacher-context';
import { getTeacherPendingListCutoffIso } from 'api/profesores';
import { sortSubjectsByPending } from '../sortSubjectsByPending';

var relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

function subjectPendingAsksDisplay(subject) {
  if (!subject) return 0;
  if (typeof subject.asksCount === 'number') return subject.asksCount;
  return subject.asksCount?.length ?? 0;
}

async function fetchAsksForSubject(subjectId) {
  const query = qs.stringify(
    {
      filters: {
        subject: { id: { $eq: subjectId } },
        answered: { $null: true },
        createdAt: { $gte: getTeacherPendingListCutoffIso() },
        school: { id: { $null: true } },
      },
      pagination: { limit: -1 },
      populate: ['user', 'subject', 'material'],
    },
    { encodeValuesOnly: true }
  );
  const asksResponse = await xhr(`/asks?${query}`);
  const list = asksResponse.data?.data ?? asksResponse.data ?? [];
  return Array.isArray(list) ? list : [];
}

function AsksChildren() {
  const { counts } = useIndex();
  const sortedAsksSubjects = React.useMemo(
    () => sortSubjectsByPending(counts?.asksData, subjectPendingAsksDisplay),
    [counts?.asksData]
  );
  const [currentAskSubject, setCurrentAskSubject] = React.useState('');
  const [asksData, setAsksData] = React.useState([]);
  const [isAsksFetching, setIsAsksFetching] = React.useState(false);
  const [studentIdByUserId, setStudentIdByUserId] = React.useState({});
  const [isStudentsFetching, setIsStudentsFetching] = React.useState(false);

  React.useEffect(() => {
    const first = sortedAsksSubjects[0];
    if (!first?.id) {
      setCurrentAskSubject(null);
      setAsksData([]);
      return undefined;
    }
    setCurrentAskSubject(first);
    setIsAsksFetching(true);
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchAsksForSubject(first.id);
        if (!cancelled) setAsksData(rows);
      } catch (e) {
        if (!cancelled) setAsksData([]);
      } finally {
        if (!cancelled) setIsAsksFetching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortedAsksSubjects]);

  React.useEffect(() => {
    const loadStudentIds = async () => {
      try {
        if (!asksData?.length) {
          setStudentIdByUserId({});
          return;
        }

        const userIds = [...new Set(asksData.map(a => a?.user?.id).filter(Boolean))];
        if (!userIds.length) return;

        setIsStudentsFetching(true);

        const inParams = userIds
          .map((id) => `filters[user][id][$in]=${encodeURIComponent(id)}`)
          .join('&');

        const url =
          `/students?${inParams}` +
          `&populate=user` +
          `&pagination[pageSize]=${encodeURIComponent(Math.max(userIds.length, 25))}`;

        const res = await xhr(url);
        const list = res?.data?.data || [];

        const map = {};
        for (const item of list) {
          const studentId = item?.id;
          const userId = item?.user?.id ?? item?.user;
          if (userId && studentId) map[userId] = studentId;
        }


        setStudentIdByUserId(map);
      } catch (e) {
        setStudentIdByUserId({});
      } finally {
        setIsStudentsFetching(false);
      }
    };

    loadStudentIds();
  }, [asksData]);

  const handleAskSubject = async (subject) => {
    setIsAsksFetching(true);
    setCurrentAskSubject(subject);
    try {
      const rows = await fetchAsksForSubject(subject.id);
      setAsksData(rows);
    } catch (error) {
      setAsksData([]);
    } finally {
      setIsAsksFetching(false);
    }
  };

  if (!counts)
    return (
      <Flex justifyContent="center" marginTop="lg">
        <Spinner size="xl" />
      </Flex>
    );

  return (
    <Box>
      <Box display="flex" fontWeight="bold" fontSize="28px">
        Preguntas por responder{' '}
        <Text color="red" fontWeight="bold">
          ({counts?.asks})
        </Text>
      </Box>

      <Flex alignItems="center" maxWidth="300px" marginTop="md">
        <Menu>
          <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
            <Box display="flex">
              {currentAskSubject?.name}{' '}
              <Box use="span" marginLeft="xs" color="red">
                [{subjectPendingAsksDisplay(currentAskSubject)}]
              </Box>
            </Box>
          </MenuButton>
          <MenuList maxHeight="300px" overflowY="scroll">
            {sortedAsksSubjects.map((el) => {
              return (
                <MenuItem key={el.id} onClick={() => handleAskSubject(el)}>
                  <Flex>
                    {el.name}{' '}
                    <Box color="red" marginLeft="xs">
                      {' '}
                      [{subjectPendingAsksDisplay(el)}]
                    </Box>
                  </Flex>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      </Flex>

      {isAsksFetching ? (
        <Flex justifyContent="center" marginTop="lg">
          <Spinner size="large" />
        </Flex>
      ) : (
        <Table marginTop="10px">
          <Thead bg="gray.200">
            <Tr>
              <Th>Cod. Estudiante </Th>
              <Th>Estudiante </Th>
              <Th>Materia </Th>
              <Th>Asunto </Th>
              <Th>Fecha</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {asksData?.map((ask) => {
              const diffDate = dayjs().diff(ask.createdAt, 'day');

              return (
                <Tr key={ask.id} color={diffDate > 1 && 'red'}>
                  <Td>{isStudentsFetching ? <Spinner size="sm" /> : (studentIdByUserId[ask.user.id] ?? '-')}</Td>
                  <Td>{ask.user.first_name}</Td>
                  <Td>{ask.subject.name}</Td>
                  <Td>{ask.matter}</Td>
                  <Td>{dayjs(ask.createdAt).fromNow()}</Td>
                  <Td>
                    <Link to={`/plataforma/profesores/ver-mensaje/${ask.id}?type=${ask.material?.type}`}>
                      <Button>Ver</Button>
                    </Link>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}

export default function AsksTeacher() {
  return (
    <TeacherProvider>
      <AsksChildren />
    </TeacherProvider>
  );
}
